import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useMutation } from "@apollo/client";
import { Typography } from "@mui/material";
import { Chip, ConfirmDialog, DataTable, toast } from "@outpave/ds";
import type { DataColumn, RowAction } from "@outpave/ds";
import {
  REMOVE_CARD_PROGRAM,
  START_CARD_PROGRAM,
} from "graphql/cardProgramAdmin";
import CardProgramStatus from "enums/CardProgramStatus";
import Role from "enums/Role";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import useCardPrograms, { CardProgram } from "hooks/useCardPrograms";
import { cardProgramUploadPath } from "utils/cardPrograms/cardProgramPaths";
import AddCardProgramDrawer from "./AddCardProgramDrawer";

const statusTone = (status?: CardProgramStatus) => {
  if (status === CardProgramStatus.ACTIVE) return "positive";
  if (status === CardProgramStatus.INACTIVE) return "neutral";
  return "warning";
};

const WAITING_STATUSES = [
  CardProgramStatus.PENDING,
  CardProgramStatus.PENDING_REVIEW,
];
const SENT_CONNECTION_STATUSES = [
  CardProgramStatus.PENDING_REVIEW,
  CardProgramStatus.ACTIVE,
];

const awaitsIssuerApproval = ({ status, connection }: CardProgram) =>
  !!status &&
  WAITING_STATUSES.includes(status) &&
  !!connection?.status &&
  SENT_CONNECTION_STATUSES.includes(connection.status);

interface CardProgramsTableProps {
  /** The page header holds Add card program, so it owns whether the drawer is open. */
  adding: boolean;
  onAddClose: () => void;
}

/**
 * The card programs a company holds. A company with one sees the one it has
 * always had; adding a second is what this surface exists for.
 */
const CardProgramsTable: React.FC<CardProgramsTableProps> = ({
  adding,
  onAddClose,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isOwner = useCheckUserRole(Role.Owner);
  const isAdmin = useCheckUserRole(Role.Admin);
  const { programs, loading, refetch } = useCardPrograms();

  const [confirmRemove, setConfirmRemove] = useState<CardProgram | null>(null);
  const [removing, setRemoving] = useState(false);

  const [startCardProgram] = useMutation(START_CARD_PROGRAM);
  const [remove] = useMutation(REMOVE_CARD_PROGRAM);

  const handleStart = async (input: {
    platformIntegrationId: string;
    productKey: string;
    accountKey?: string;
  }) => {
    const result = await startCardProgram({ variables: { input } });
    await refetch();
    return result.data?.startCardProgram;
  };

  const handleRemove = async () => {
    if (!confirmRemove) return;
    setRemoving(true);
    try {
      await remove({ variables: { id: confirmRemove.id } });
      await refetch();
      setConfirmRemove(null);
      toast.success(t("CardProgramRemovedToast"));
    } catch (error: unknown) {
      toast.error(
        error instanceof Error ? error.message : t("Please try again"),
      );
    } finally {
      setRemoving(false);
    }
  };

  const columns: DataColumn<CardProgram>[] = useMemo(
    () => [
      {
        id: "nickname",
        header: t("Program name"),
        cell: (program) => ({
          content: (
            <Typography variant="subtitle2">
              {program.nickname || t("Card program")}
            </Typography>
          ),
          caption: program.last4 ? `•••• ${program.last4}` : undefined,
        }),
      },
      {
        id: "type",
        header: t("Program type"),
        cell: (program) =>
          program.subAccountType ? t(program.subAccountType) : "-",
      },
      {
        id: "status",
        header: t("Verification status"),
        cell: (program) => ({
          content: program.actionRequired ? (
            <Chip size="sm" dot tone="warning" label={t("Action required")} />
          ) : (
            <Chip
              size="sm"
              dot
              tone={statusTone(program.status)}
              label={t(program.status ?? CardProgramStatus.PENDING)}
            />
          ),
          caption:
            awaitsIssuerApproval(program) && !program.actionRequired
              ? t("CardProgramActivatesOnApproval")
              : undefined,
        }),
      },
      {
        id: "default",
        header: t("Default"),
        cell: (program) =>
          program.isDefault ? (
            <Chip size="sm" tone="neutral" label={t("Default")} />
          ) : (
            "-"
          ),
      },
    ],
    [t],
  );

  const rowActions = (program: CardProgram): RowAction[] => {
    const { connection } = program;
    return [
      ...(isAdmin && program.actionRequired && connection
        ? [
            {
              label: t("Continue verification"),
              onClick: () =>
                navigate(cardProgramUploadPath(connection.id, program.id)),
            },
          ]
        : []),
      ...(isOwner && !program.isDefault
        ? [
            {
              label: t("Remove card program"),
              onClick: () => setConfirmRemove(program),
            },
          ]
        : []),
    ];
  };

  return (
    <>
      <DataTable<CardProgram>
        columns={columns}
        rows={programs}
        getRowKey={(program) => program.id}
        loading={loading}
        rowActions={rowActions}
        rowActionsLabel={t("Actions")}
        emptyState={t("NoCardProgramsYet")}
        aria-label={t("Card programs")}
      />

      <AddCardProgramDrawer
        open={adding}
        onClose={onAddClose}
        onStart={handleStart}
      />

      <ConfirmDialog
        open={!!confirmRemove}
        tone="danger"
        title={t("Remove card program")}
        message={t("RemoveCardProgramConfirm")}
        confirmLabel={t("Remove")}
        cancelLabel={t("Cancel")}
        busy={removing}
        onConfirm={handleRemove}
        onCancel={() => setConfirmRemove(null)}
      />
    </>
  );
};

export default CardProgramsTable;
